<template>
  <div
    class="yard-view-spot-select-wrapper d-flex flex-column gap-2"
    data-testid="yard-view-asset-select-wrapper">
    <v-autocomplete
      data-testid="yard-view-asset-select"
      color="primary"
      density="compact"
      item-title="assetCode"
      item-value="assetId"
      variant="outlined"
      :clearable="clearable"
      :disabled="disabled"
      :hide-details="hideDetails"
      :hint="hint"
      :items="assets"
      :model-value="modelValue"
      :persistent-hint="persistentHint"
      :placeholder="placeholder"
      :required="required"
      :rules="rules"
      @update:model-value="handleValueChange">
      <template #no-data>
        <v-list-item :title="t('yard.messages.noAssetsAvailable')"></v-list-item>
      </template>

      <template #selection="{ index }">
        <span v-if="index === 0" class="typography-body-md">
          {{ getRenderedSelectedValue() }}
        </span>
      </template>

      <template #item="{ item, props }">
        <v-list-item v-bind="props" :disabled="item.raw.disabled">
          <template #prepend>
            <v-icon size="x-small">{{ item.raw.icon }}</v-icon>
          </template>

          <template #title>
            <div class="d-flex flex-row align-center justify-space-between gap-4">
              <span class="typography-body-md">{{ item.title }}</span>
              <span class="typography-body-sm">
                {{
                  item.raw.isAssigned
                    ? `${item.raw.spotAreaName} - ${item.raw.spotCode}`
                    : t('yard.drawers.createTaskDrawer.labels.waitingAssignment')
                }}
              </span>
            </div>
          </template>
        </v-list-item>
      </template>

      <template #message="{ message }">
        <small class="d-flex typography-body-sm">
          {{ message }}
        </small>
      </template>
    </v-autocomplete>

    <!-- slot for any additional content -->
    <slot
      v-if="modelValue && selectedAsset"
      name="selected-asset"
      :selected-asset="selectedAsset"></slot>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetSelect',
  emits: ['update:modelValue', 'input'],
  props: {
    modelValue: {
      type: [String, Number],
      default: null
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Search by asset ID'
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    includeAssignedAssets: {
      type: [Boolean, String],
      default: false,
      validator: value => {
        if (['boolean', 'undefined'].includes(typeof value)) {
          return true;
        }

        return value === 'disabled';
      }
    },
    includeWaitingAssignmentAssets: {
      type: [Boolean, String],
      default: false,
      validator: value => {
        if (['boolean', 'undefined'].includes(typeof value)) {
          return true;
        }

        return value === 'disabled';
      }
    },
    includeAssociatedToActiveMoveTasks: {
      type: [Boolean, String],
      default: false,
      validator: value => {
        if (['boolean', 'undefined'].includes(typeof value)) {
          return true;
        }

        return value === 'disabled';
      }
    }
  },
  setup(props, { emit }) {
    const yardStore = useYard();
    const { t } = useI18n();

    const handleValueChange = newValue => {
      emit('update:modelValue', newValue);
      emit('input', newValue);
    };

    const isAssetAssigned = asset => {
      const spotAssignment = yardStore.getContainerSpotAssignmentText(asset.id);
      return spotAssignment.spotId !== null;
    };

    const shouldExcludeWaitingAssignmentAsset = asset => {
      return !isAssetAssigned(asset);
    };

    const shouldExcludeAssignedAsset = asset => {
      return isAssetAssigned(asset);
    };

    const isAssetInActiveMoveTask = asset => {
      if (!isAssetAssigned(asset)) {
        return false;
      }
      const spotAssignment = yardStore.getContainerSpotAssignmentText(asset.id);
      return yardStore.isSpotInActiveMoveTask(spotAssignment.spotId);
    };

    const shouldExcludeAssetAssociatedToActiveMoveTask = asset => {
      return isAssetInActiveMoveTask(asset);
    };

    const shouldDisableAsset = asset => {
      return (
        (props.includeWaitingAssignmentAssets === 'disabled' && !isAssetAssigned(asset)) ||
        (props.includeAssignedAssets === 'disabled' && isAssetAssigned(asset)) ||
        (props.includeAssociatedToActiveMoveTasks === 'disabled' && isAssetInActiveMoveTask(asset))
      );
    };

    const assets = computed(() => {
      const assetContainers = yardStore.assetContainersRef.value || [];

      const exclusions = [
        ...(props.includeWaitingAssignmentAssets ? [] : [shouldExcludeWaitingAssignmentAsset]),
        ...(props.includeAssignedAssets ? [] : [shouldExcludeAssignedAsset]),
        ...(props.includeAssociatedToActiveMoveTasks
          ? []
          : [shouldExcludeAssetAssociatedToActiveMoveTask])
      ];

      const isExcludedBySomeFilter = asset => exclusions.some(exclude => exclude(asset));

      let assets = [];
      assetContainers.forEach(asset => {
        if (isExcludedBySomeFilter(asset)) {
          return;
        }

        const spotAssignment = yardStore.getContainerSpotAssignmentText(asset.id);
        const isAssigned = isAssetAssigned(asset);

        assets.push({
          assetId: asset.id,
          assetCode: asset.code,
          assetType: asset.type,
          icon: isAssigned ? 'mdi-truck-trailer' : 'mdi-clock-outline',
          spotCode: spotAssignment.spotCode,
          spotAreaName: spotAssignment.spotAreaName,
          isAssigned,
          spotId: spotAssignment.spotId,
          disabled: shouldDisableAsset(asset)
        });
      });

      // Sort assets: assigned first (by asset code, area name, spot code), then unassigned (by asset code)
      assets.sort((a, b) => {
        // First, sort by assignment status (assigned first)
        if (a.isAssigned !== b.isAssigned) {
          return b.isAssigned - a.isAssigned;
        }

        // For assigned assets: sort by asset code, then area name, then spot code
        // For unassigned assets: sort by asset code only
        const sortKeyA = a.isAssigned
          ? `${a.assetCode}|${a.spotAreaName}|${a.spotCode}`
          : a.assetCode;
        const sortKeyB = b.isAssigned
          ? `${b.assetCode}|${b.spotAreaName}|${b.spotCode}`
          : b.assetCode;

        return sortKeyA.localeCompare(sortKeyB);
      });

      return assets;
    });

    const selectedAsset = computed(() => {
      if (!props.modelValue) {
        return null;
      }

      const selectedAsset = assets.value.find(asset => asset.assetId === props.modelValue);
      return selectedAsset ?? null;
    });

    const getRenderedSelectedValue = () => {
      return selectedAsset.value ? `${selectedAsset.value.assetCode}` : '';
    };

    onMounted(() => {
      yardStore.fetchAssetContainers();
    });

    return {
      t,
      assets,
      handleValueChange,
      getRenderedSelectedValue,
      selectedAsset
    };
  }
});
</script>
